import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import OfferDurationOptions from '../../Duration/OfferDurationOptions'
import Modal from 'LuxKit/Modal/Modal'
import { OfferPageActions, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCalendarDurationOptionsWithCharterBundleRestrictions } from 'selectors/offerPage/offerPageSelectors'
import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import { min } from 'lib/array/arrayUtils'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  duration: number
  durationOptions: Array<OfferDurationOption>
}

function OfferCalendarDurationModal(props: Props) {
  const { durationOptions } = props

  const intl = useIntl()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const minDuration = useMemo(() => min(durationOptions, duration => duration.duration)?.duration!, [durationOptions])
  const modalElement = useModalElementContext()

  const onDurationSelect = useCallback(() => {
    dispatch({ type: OfferPageActions.closeSubFilter })
    modalElement?.resolve()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenDuration)
  }, [dispatch, modalElement, onUserAnalyticsAction])

  const durationModalTitle = intl.formatMessage({
    id: '5VtkBPvGOuuALAu930cW',
    defaultMessage: 'How long do you want to stay?',
    description: 'Title for duration selection modal in offer calendar',
  })
  const durationModalSubtitle = minDuration > 1 ? intl.formatMessage(
    {
      id: 'r9B0jsapGGM8vdgY6lkF',
      defaultMessage: 'Minimum stay for this offer is {minStay, plural, one {# night} other {# nights}}',
      description: 'Subtitle showing minimum stay duration for offer calendar duration modal',
    },
    { minStay: minDuration },
  ) : undefined

  return <Modal
    title={durationModalTitle}
    subtitle={durationModalSubtitle}
  >
    <OfferDurationOptions onSelect={onDurationSelect} />
  </Modal>
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    durationOptions: getCalendarDurationOptionsWithCharterBundleRestrictions(currentState, appState),
  }
}, OfferCalendarDurationModal)
